// =============================================================
// 📁 src/lib/axios.ts — Axios sécurisé (Access + Refresh)
// 🌟 Platinum PRO 2025 — Version finale fusionnée & stable
// =============================================================

import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";
import { toast } from "react-toastify";

/* ==========================================================
   🌍 Configuration globale
========================================================== */
export const API_BASE_URL =
  import.meta.env.VITE_API_URL?.replace(/\/+$/, "") || "";

/**
 * `baseURL` = `VITE_API_URL` (souvent `/api` en dev). Les chemins (`url`) doivent
 * continuer après ce préfixe sans le répéter (ex. `/admin/...`), sinon on obtient `/api/api/...`.
 */

/** Code JSON `error` pour 403 « action réservée administrateur » (RBAC géographie / zones). */
export const API_ERROR_ACTION_RESERVED_ADMIN = "ACTION_RESERVED_ADMIN";

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // ✅ nécessaire pour cookies httpOnly
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 20000,
});
 
/* ==========================================================
   🔐 Stockage : jeton métier (admin / staff) vs portail client B2C
========================================================== */
let accessToken: string | null = localStorage.getItem("accessToken");
let clientAccessToken: string | null = localStorage.getItem("clientAccessToken");

export const setAccessToken = (token: string | null) => {
  accessToken = token;

  if (token) {
    localStorage.setItem("accessToken", token);
  } else {
    localStorage.removeItem("accessToken");
  }
};

/** Jeton réservé au rôle `client` — ne jamais réutiliser le token admin pour /client-portal. */
export const setClientAccessToken = (token: string | null) => {
  clientAccessToken = token;

  if (token) {
    localStorage.setItem("clientAccessToken", token);
  } else {
    localStorage.removeItem("clientAccessToken");
  }
};

function bearerForCurrentRole(): string | null {
  const role = localStorage.getItem("role")?.toLowerCase();
  if (role === "client") {
    return clientAccessToken ?? localStorage.getItem("clientAccessToken");
  }
  return accessToken ?? localStorage.getItem("accessToken");
}

/* ==========================================================
   🔑 Intercepteur REQUEST — Injection du Bearer Token
========================================================== */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const bearer = bearerForCurrentRole();
    if (bearer) {
      if (config.headers instanceof AxiosHeaders) {
        config.headers.set("Authorization", `Bearer ${bearer}`);
      } else {
        (config.headers as any)["Authorization"] = `Bearer ${bearer}`;
      }
    }
    if (config.data instanceof FormData) {
      if (config.headers instanceof AxiosHeaders) {
        config.headers.delete("Content-Type");
      } else {
        delete (config.headers as Record<string, unknown>)["Content-Type"];
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/* ==========================================================
   ♻️ Intercepteur RESPONSE — Refresh automatique sécurisé
========================================================== */
let isRefreshing = false;
let failedQueue: Array<(token: string | null) => void> = [];

const processQueue = (token: string | null) => {
  failedQueue.forEach((cb) => cb(token));
  failedQueue = [];
};

/** Ne pas enchaîner sur POST /auth/refresh pour les routes « auth publique » ou pour éviter une boucle sur refresh. */
function shouldAttemptTokenRefresh(cfg: InternalAxiosRequestConfig | undefined): boolean {
  if (!cfg) return false;
  const url = String(cfg.url ?? "");
  if (url.includes("/auth/login")) return false;
  if (url.includes("/auth/refresh")) return false;
  if (url.includes("/auth/forgot-password")) return false;
  if (url.includes("/auth/register")) return false;
  if (url.includes("/auth/set-password")) return false;
  if (url.includes("/auth/reset-password")) return false;
  return true;
}

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    const originalRequest: any = error.config;
    const reqUrl = String(originalRequest?.url ?? "");

    // 🔁 Gestion du refresh automatique (uniquement pour les requêtes authentifiées, pas après login raté)
    if (status === 401 && !originalRequest?._retry && shouldAttemptTokenRefresh(originalRequest)) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          failedQueue.push((token) => {
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              resolve(api(originalRequest));
            }
          });
        });
      }

      isRefreshing = true;

      try {
        // ✅ Utiliser la même instance api (évite double baseURL)
        const refreshRes = await api.post("/auth/refresh");

        const newAccessToken = (refreshRes.data as any)?.accessToken;
        if (!newAccessToken) throw new Error("Aucun nouveau token reçu");

        const role = localStorage.getItem("role")?.toLowerCase();
        if (role === "client") {
          setClientAccessToken(newAccessToken);
        } else {
          setAccessToken(newAccessToken);
        }
        processQueue(newAccessToken);

        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(null);

        // 🔥 Nettoyage ciblé (pas de localStorage.clear)
        setAccessToken(null);
        setClientAccessToken(null);
        localStorage.removeItem("user");
        localStorage.removeItem("role");
        localStorage.removeItem("clientPortalSession");

        toast.dismiss();
        toast.warning("🔑 Session expirée. Veuillez vous reconnecter.");

        window.dispatchEvent(new Event("sessionExpired"));

        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    /* ======================================================
       ⚠️ Gestion des autres erreurs
    ====================================================== */
    if (originalRequest?.skipGlobalErrorToast === true) {
      return Promise.reject(error);
    }

    const data = error.response?.data as Record<string, any> | undefined;
    const message =
      data?.error || data?.message || error.message || "Erreur inconnue";

    switch (status) {
      case 0:
        toast.error("🌐 Serveur injoignable.");
        break;
      case 400: {
        const m = data?.message;
        if (typeof m === "string" && m.trim().length > 0) {
          toast.error(m.trim());
          break;
        }
        if (data?.error === "VALIDATION_ERROR") {
          toast.error("Données invalides. Vérifiez les champs envoyés (photos, GPS).");
          break;
        }
        if (typeof data?.error === "string" && data.error.trim().length > 0) {
          toast.error(data.error.trim());
          break;
        }
        toast.error("⚠️ Requête invalide.");
        break;
      }
      case 401:
        /* Déconnexion uniquement après échec du refresh (voir bloc ci-dessus). Pas de toast générique ici. */
        break;
      case 403:
        if (data?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
          const m =
            typeof data?.message === "string" && data.message.trim().length > 0
              ? data.message
              : "Action réservée administrateur";
          toast.warning(m);
          break;
        }
        /* Autres 403 : les écrans métier peuvent afficher AccessDenied ; pas de logout. */
        break;
      case 404:
        toast.info("❓ Ressource introuvable.");
        break;
      case 500:
        toast.error(
          typeof message === "string" && message.length > 0 && message !== "Erreur inconnue"
            ? `Erreur métier : ${message}`
            : "Erreur métier — réessayez ou contactez le support."
        );
        break;
      default:
        console.warn(`⚠️ Erreur API (${status}) :`, message);
        break;
    }

    return Promise.reject(error);
  }
);

export default api;